import { BoundNumberField, Css } from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { useMemo } from "react";
import {
  SaveUnderwritingConfigInput,
  UnderwritingOverviewPageUnderwritingConfigFragment,
  useSaveUnderwritingConfigMutation,
  useUnderwritingOverviewUnderwritingConfigQuery,
} from "src/generated/graphql-types";

type UnderwritingDetailsProps = {
  underwritingConfig: UnderwritingOverviewPageUnderwritingConfigFragment;
};

export type SaveUnderwritingDetails = SaveUnderwritingConfigInput;

export function UnderwritingDetails({ underwritingConfig }: UnderwritingDetailsProps) {
  const query = useUnderwritingOverviewUnderwritingConfigQuery({
    variables: { underwritingConfigId: underwritingConfig.id },
  });
  const [saveUnderwritingMutation] = useSaveUnderwritingConfigMutation();

  const formConfig: ObjectConfig<SaveUnderwritingDetails> = useMemo(
    () => ({
      id: { type: "value" },
      targetInvestorMarginBasisPoints: { type: "value", rules: [required] },
      debtFacilityBaseRateBasisPoints: { type: "value", rules: [required] },
      debtFacilitySpreadBasisPoints: { type: "value", rules: [required] },
      buildMarginBasisPoints: { type: "value", rules: [required] },
      siteAcquisitionMarkupBasisPoints: { type: "value", rules: [required] },
      weightedFinancingRate: { type: "value", rules: [required] },
      financingRate: { type: "value", rules: [required] },
      percentDebtFinancedBasisPoints: { type: "value", rules: [required] },
      preConMonths: { type: "value", rules: [required] },
      constructionMonths: { type: "value", rules: [required] },
      salesPeriodMonths: { type: "value", rules: [required] },
      exitRealtorFeeBasisPoints: { type: "value", rules: [required] },
      acquisitionClosingCostsBasisPoints: { type: "value", rules: [required] },
    }),
    [],
  );

  const formState = useFormState({
    config: formConfig,
    init: {
      query,
      map: ({ underwritingConfigs: [underwritingConfig] }) => ({
        ...underwritingConfig,
      }),
    },
    autoSave: saveUnderwriting,
  });

  async function saveUnderwriting() {
    await saveUnderwritingMutation({
      variables: { input: formState.value },
    });
  }

  return (
    <div css={Css.df.aic.$}>
      <div css={Css.df.wPx(400).fs0.fdc.p2.bgGray100.$}>
        <div css={Css.df.fdc.gap3.$}>
          <BoundNumberField
            field={formState.targetInvestorMarginBasisPoints}
            type="basisPoints"
            label="Default Target Investor Margin"
          />
          <BoundNumberField
            field={formState.debtFacilityBaseRateBasisPoints}
            type="basisPoints"
            label="Default Base Rate"
          />
          <BoundNumberField
            field={formState.debtFacilitySpreadBasisPoints}
            type="basisPoints"
            label="Default Spread Rate"
          />
          <BoundNumberField field={formState.buildMarginBasisPoints} type="basisPoints" label="Build Margin" />
          <BoundNumberField
            field={formState.siteAcquisitionMarkupBasisPoints}
            type="basisPoints"
            label="Site Acquisition Markup"
          />
          <BoundNumberField
            field={formState.weightedFinancingRate}
            label="Weighted Financing Rate"
            numFractionDigits={5}
          />
          <BoundNumberField field={formState.financingRate} label="Financing Rate" numFractionDigits={5} />
          <BoundNumberField
            field={formState.percentDebtFinancedBasisPoints}
            type="basisPoints"
            label="Percent Debt Financed"
          />
          <BoundNumberField field={formState.preConMonths} label="Pre-Construction Months" numFractionDigits={2} />
          <BoundNumberField field={formState.constructionMonths} label="Construction Months" numFractionDigits={2} />
          <BoundNumberField field={formState.salesPeriodMonths} label="Sales Period Months" numFractionDigits={2} />
          <BoundNumberField field={formState.exitRealtorFeeBasisPoints} type="basisPoints" label="Exit Realtor Fee" />
          <BoundNumberField
            field={formState.acquisitionClosingCostsBasisPoints}
            type="basisPoints"
            label="Acquisition Closing Costs"
          />
        </div>
      </div>
    </div>
  );
}
